import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserPreferences } from './useUserPreferences';
import type { ThemePreference } from './user-preferences';

export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'nx:theme-pref';

function writePersisted(pref: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* ignore */
  }
}

function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

function resolveSystem(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Resolve the effective theme (light/dark) from the user's preference.
 * Subscribes to `prefers-color-scheme` when `theme === 'system'` so the
 * page flips live with the OS. Applies `data-theme` to
 * `document.documentElement` for CSS scoping.
 *
 * Persistence: the raw preference (`system`/`light`/`dark`) is mirrored
 * to `localStorage` under `nx:theme-pref` so the inline pre-mount
 * script in `index.html` can read it before React loads and avoid a
 * theme flash on page open. The user-preferences store is the source
 * of truth once React is mounted.
 */
export function useTheme() {
  const { prefs, update } = useUserPreferences();
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => resolveSystem());

  // Listen for OS theme flips when running in `system` mode.
  useEffect(() => {
    if (prefs.theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [prefs.theme]);

  const effective: ResolvedTheme = prefs.theme === 'system' ? systemTheme : prefs.theme;

  // Apply to <html data-theme="..."> on every change.
  useEffect(() => {
    applyTheme(effective);
  }, [effective]);

  const setTheme = useCallback(
    (next: ThemePreference) => {
      update({ theme: next });
      writePersisted(next);
    },
    [update],
  );

  return useMemo(
    () => ({
      preference: prefs.theme,
      effective,
      setTheme,
    }),
    [prefs.theme, effective, setTheme],
  );
}
